import React, { PropsWithChildren } from 'react';
import classNames from 'classnames';
import { CSeverity } from '@/src/components/interfaces';
import { CIcon, CIcons } from '@/src/components/c-icon/CIcon';
import { AlignItems, CFlex, FlexDirection, JustifyContent } from '@/src/components/c-flex/CFlex';
import { CBox } from '@/src/components/c-box/CBox';
import { CText, CTextSize } from '@/src/components/c-text/CText';

export enum FieldValidationPattern {
  Icon = 'icon',
  List = 'list',
}

export interface FieldValidation {
  severity: CSeverity;
  message: string;
  pattern?: FieldValidationPattern;
}

export interface CFieldValidationProps {
  id?: string;
  class?: string;
  testSelector?: string;
  validations: FieldValidation[];
}

export function CFieldValidation(props: PropsWithChildren<CFieldValidationProps>): React.JSX.Element {
  const icons = {
    [CSeverity.Info]: undefined,
    [CSeverity.Success]: CIcons.Smile,
    [CSeverity.Warning]: CIcons.Meh,
    [CSeverity.Error]: CIcons.Frown,
  };

  const validations = props.validations.map(validation => ({
    severity: validation.severity,
    message: validation.message,
    pattern: validation.pattern ?? FieldValidationPattern.Icon,
  }));

  return (
    <CBox
      id={props.id}
      class={classNames('c-field-validation', props.class)}
      fullWidth={true}
      data-test-selector={props.testSelector}
    >
      <CFlex direction={FlexDirection.Column} class="c-gap--xs">
        {validations.map(validation => (
          <CBox key={`${validation.severity}-${validation.message}`}>
            <CFlex class="c-gap--xs" alignItems={AlignItems.Center}>
              {validation.pattern === FieldValidationPattern.Icon && validation.severity !== CSeverity.Info && (
                <CIcon icon={icons[validation.severity]} color={validation.severity}/>
              )}
              {validation.pattern === FieldValidationPattern.List && (
                <CFlex justifyContent={JustifyContent.Center} alignItems={AlignItems.Center} class="c-field-validation__dot-container">
                  <div
                    className={classNames('c-field-validation__dot', {
                      [`c-field-validation__dot--${validation.severity}`]: validation.severity,
                    })}
                  />
                </CFlex>
              )}
              <CText
                size={CTextSize.Sm}
                testSelector="field-validation-message"
                class={classNames({
                  [`c-field-validation__message-${validation.severity}`]: !!validation.severity,
                })}
              >
                {validation.message}
              </CText>
            </CFlex>
          </CBox>
        ))}
      </CFlex>
    </CBox>
  );
}